import { z } from "zod";

import {
  Metric,
  MetricGroup,
  type MetricGroupProps,
  type MetricProps,
} from "@/components/ui/chat/metric/Metric";

/** Herald chat catalog definition for Metric. Use in renderer catalog. */
export const ChatMetricSpec = {
  props: z.object({
    label: z.string(),
    value: z.string(),
    variant: z.enum(["default", "delta"]).nullable().optional(),
    format: z.enum(["currency", "percent", "number"]).nullable(),
    trend: z.enum(["up", "down", "neutral"]).nullable(),
    invertTrend: z.boolean().nullable().optional(),
    subtitle: z.string().nullable().optional(),
    previousValue: z.string().nullable().optional(),
    delta: z.string().nullable().optional(),
    deltaPercent: z.string().nullable().optional(),
  }),
  description:
    "Single metric: optional variant 'default' | 'delta' (omit or null = default). default: label, value (text-base bold), trend icon beside value, optional single comparison line from previousValue + delta + deltaPercent, optional subtitle. delta: period-over-period layout — large value + inline 'from previousValue', then trend icon + pill for delta/(deltaPercent); trend may be up | down | neutral. Set invertTrend=true when an increase is bad (e.g. negative coverage rising). Value can be numeric or text.",
  example: {
    label: "Trending Stories",
    value: "20",
    format: "number",
    trend: "up",
    subtitle: "Upward trajectory, last 30 days",
  },
} as const;

/** Adapter for json-render defineRegistry: forwards props to Metric. */
export const ChatMetricFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => <Metric {...(props as unknown as MetricProps)} />;

const metricGroupItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  variant: z.enum(["default", "delta"]).nullable().optional(),
  format: z.enum(["currency", "percent", "number"]).nullable().optional(),
  trend: z.enum(["up", "down", "neutral"]).nullable().optional(),
  invertTrend: z.boolean().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  previousValue: z.string().nullable().optional(),
  delta: z.string().nullable().optional(),
  deltaPercent: z.string().nullable().optional(),
});

/** Herald chat catalog definition for MetricGroup. Use in renderer catalog. */
export const ChatMetricGroupSpec = {
  props: z.object({
    items: z.array(metricGroupItemSchema),
  }),
  description:
    "Multiple metric rows in one row (no section title, no card). Each item: label, value, optional variant ('default' | 'delta'), optional format, trend (up | down | neutral), optional previousValue/delta/deltaPercent for PoP, optional subtitle.",
  example: {
    items: [
      { label: "Total Articles", value: "5418", format: "number" },
      { label: "Positive", value: "14.3", format: "percent", trend: "up" },
      { label: "Negative", value: "17.4", format: "percent", trend: "down" },
      { label: "Neutral", value: "68.3", format: "percent" },
    ],
  },
} as const;

/** Adapter for json-render defineRegistry: forwards props to MetricGroup. Full width so it takes a row in chat flex layout. */
export const ChatMetricGroupFromProps = ({
  props,
}: {
  props: Record<string, unknown>;
}) => (
  <div className="w-full">
    <MetricGroup {...(props as unknown as MetricGroupProps)} />
  </div>
);
